import React, { useRef, useState } from 'react'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, useSlow, Failed } from '../../components/pending'
import o from '../../components/Overlay.module.css'
import ps from './PersonnelSettingsModal.module.css'
import { apiJson } from '../../lib/api'
import { money, plural } from '../wages/format'
import { JobRolesEditor } from './JobRolesEditor'

// The Personnel cog (V4 pplCog, §7j.2d). The default minimum rate belongs
// with the people it applies to, not with the wage calculation that consumes
// it; Wages shows it read-only. It is stored in wage_settings and saved
// through the same audited route as the Wages cog, so a change requeues
// every unlocked worker/day. Modal vocabulary is shared with the Wages cog.
// The job roles on offer live here too (D5 B-1(a)); each role change is
// saved as it is made, so Save settings only ever saves the rate.

interface Props {
  user: any
  // The rate needs Wages edit with pay data; without it only the roles show.
  showRate: boolean
  current: number | null
  withoutRate: number
  onClose: () => void
  onSaved: (msg: string) => void
}

export function PersonnelSettingsModal({ user, showRate, current, withoutRate, onClose, onSaved }: Props) {
  const [rate, setRate] = useState(current != null ? String(current) : '')
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const [error, setError] = useState<string | null>(null)
  const [rolesDirty, setRolesDirty] = useState(false)
  // A layer (h 425-458, 3975-4002): Escape, the scrim and Cancel ask first once the rate or an unsaved role name has changed, and are blocked while save is pending.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(rate) || rolesDirty
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })

  const save = async () => {
    const v = Number(rate.trim().replace(/^\$/, ''))
    // V4 falls back to a demo constant on bad input; a real floor is never
    // guessed, so an empty or non-positive rate is refused here instead.
    if (!rate.trim() || !Number.isFinite(v) || v <= 0) {
      setError('Enter a rate per hour above $0.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<{ default_min_rate: number | null; changed: string[]; queued: number }>(user, '/api/wages/settings', {
        body: { default_min_rate: v },
      })
      onSaved(
        'Workers added without a minimum rate will get ' + money(r.default_min_rate) + '/h' +
          (r.queued ? ` · ${plural(r.queued, 'worker/day', 'worker/days')} queued for recalculation` : '')
      )
    } catch (e: any) {
      setError(e?.message || 'Could not save the settings')
      setBusy(false)
    }
  }

  const count = withoutRate
    ? plural(withoutRate, 'worker currently has', 'workers currently have') + ' no rate of their own'
    : 'Every worker currently has their own rate'

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${ps.modal}`} role="dialog" aria-label="Personnel settings">
          <div className={o.head}>
            <div className={o.headText}>
              <h2 className={o.title}>Personnel settings</h2>
              {showRate && <p className={o.sub}>Every worker can carry their own agreed hourly rate on their record. This is the fallback for anyone who does not.</p>}
            </div>
            <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className={o.body}>
            {showRate && (
              <>
                <div className={o.field}>
                  <label className={o.label} htmlFor="ppl-min-rate">
                    Workers added without a minimum rate will get this rate
                  </label>
                  <input
                    id="ppl-min-rate"
                    className={`${o.input} ${ps.rate}`}
                    inputMode="decimal"
                    value={rate}
                    placeholder="Not set"
                    onChange={(e) => setRate(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') save()
                    }}
                  />
                  <div className={o.fieldHint}>
                    {count}. Wages assesses every worker against the higher of this rate and their own, so it also acts as the floor for a rate set below it.
                  </div>
                </div>

                <div className={o.warn}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
                  </svg>
                  <span>
                    Minimum wage is set by law and differs by country, state and often by employment agreement. Dataphyll will apply whatever rate you enter. It does not check it against any jurisdiction. Making sure it meets your obligations is your responsibility.
                  </span>
                </div>
              </>
            )}

            <JobRolesEditor user={user} onDirty={setRolesDirty} />
          </div>

          <div className={o.foot}>
            <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
              {showRate ? 'Cancel' : 'Close'}
            </button>
            {showRate && (
              <PendingButton className={o.primary} busy={busy} pending="Saving…" onClick={save}>
                Save settings
              </PendingButton>
            )}
          </div>
          <Failed text={error} />
        </div>
      </div>
      {confirm}
    </>
  )
}
